import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { AbstractControl, FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { LookupResponse } from '@core/models/common.model';
import { SharedMaterialUIModule } from '@shared/modules/shared-material-ui.module';

// What is written down once an item is ticked: nothing, a result and notes, notes, or a detail for the item "Other".
export type ChecklistDetail = 'none' | 'result-notes' | 'notes' | 'other-detail';

// A list the clinician ticks from (screenings, support services, special needs): each ticked item is a row of the form array, made by
// the form so the rows stay part of its value. "grid" shows the items in two columns with their details beneath each; "inline" shows
// short items side by side, and the details of the ticked ones below.
@Component({
  selector: 'cia-checklist',
  imports: [NgTemplateOutlet, ReactiveFormsModule, ...SharedMaterialUIModule],
  templateUrl: './cia-checklist.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class CiaChecklistComponent {
  // What can be ticked.
  readonly items = input.required<LookupResponse[]>();
  // The form array holding one row for each ticked item.
  readonly rows = input.required<FormArray>();
  // The field of a row that holds the ticked item's id.
  readonly typeField = input.required<string>();
  // Makes the row for an item when it is ticked.
  readonly createRow = input.required<(typeId: string) => FormGroup>();
  // What is written down for a ticked item.
  readonly detail = input<ChecklistDetail>('none');
  readonly layout = input<'grid' | 'inline'>('grid');
  readonly readOnly = input(false);
  // The text shown beside the tick box.
  readonly label = input<(item: LookupResponse) => string>((item) => item.name ?? item.code ?? '');

  // The row of a ticked item, or nothing when it is not ticked.
  protected rowFor(typeId: string): AbstractControl | null {
    const field = this.typeField();
    return this.rows().controls.find((row) => row.get(field)?.value === typeId) ?? null;
  }

  // Whether this item has anything to write once it is ticked.
  protected hasDetail(item: LookupResponse): boolean {
    const detail = this.detail();
    return detail === 'result-notes' || detail === 'notes' || (detail === 'other-detail' && item.code === 'OTHER');
  }

  // The ticked items that have details to show (the inline layout lists them below the ticks).
  protected tickedWithDetail(): LookupResponse[] {
    return this.items().filter((item) => this.rowFor(item.id) && this.hasDetail(item));
  }

  // Ticking adds the item's row to the form array; unticking removes it.
  protected toggle(typeId: string, checked: boolean): void {
    const existing = this.rowFor(typeId);
    const rows = this.rows();

    if (checked && !existing) {
      rows.push(this.createRow()(typeId));
    } else if (!checked && existing) {
      rows.removeAt(rows.controls.indexOf(existing));
    }
  }
}
